import React from 'react';
import { Body, Card, ColumnDefinition, IconButton, RowDefinition, Select, Table } from '@indigo-ag/component-module';
import { AgFinancingDocumentType } from '@indigo-ag/schema';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';

import i18next from 'core/i18n';
import { documentRequestTypesOptions, TABLE_ACTIONS, text, UploadDocumentsCustomActions } from 'shared';
import { createStylesHook } from 'shared/hooks/createStylesHook';

export const useStyles = createStylesHook(() => ({
  noDocumentsCardContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 250
  }
}));

export interface DocumentToUpload {
  id: string;
  file: File;
  documentType?: AgFinancingDocumentType | null;
}

export type DocumentToUploadColumnId = 'file' | 'documentType' | 'removeDocumentToUpload';

const columnDefinition: ColumnDefinition<
  DocumentToUpload,
  DocumentToUploadColumnId,
  {},
  UploadDocumentsCustomActions
>[] = [
  {
    id: 'file',
    header: i18next.t('file'),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.file.name} />
  },
  {
    id: 'documentType',
    header: i18next.t('documentType'),
    cell: ({ row, onTableAction }) => (
      <Select
        hideLabel={true}
        label={i18next.t('documentType')}
        name="documentType"
        options={documentRequestTypesOptions}
        onSelectionChanged={(s: AgFinancingDocumentType | null) =>
          onTableAction({
            type: TABLE_ACTIONS.SET_DOCUMENT_TO_UPLOAD_TYPE,
            payload: { id: row.id, documentType: s }
          })
        }
        placeholder={i18next.t('pleaseSelect')}
        sizeVariant="slim"
        value={row.documentType}
      />
    )
  },
  {
    id: 'removeDocumentToUpload',
    header: '',
    cell: ({ row, onTableAction }) => (
      <IconButton
        colorVariant="primary"
        icon={faTrashAlt}
        label="remove document to upload"
        onClick={() =>
          onTableAction({
            type: TABLE_ACTIONS.REMOVE_DOCUMENT_TO_UPLOAD,
            payload: row.id
          })
        }
      />
    )
  }
];

const rowDefinition: RowDefinition<DocumentToUpload, DocumentToUploadColumnId> = {
  rowId: ({ row }) => row.id
};

export interface DocumentsToUploadListProps {
  documentsToUpload: DocumentToUpload[];
  onTableAction?: (action: any) => void;
}

export const DocumentsToUploadList: React.FunctionComponent<DocumentsToUploadListProps> = ({
  documentsToUpload,
  onTableAction
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  return (
    <Table
      label={i18next.t('documentsToUpload')}
      data={documentsToUpload}
      columnDefinition={columnDefinition}
      onTableAction={onTableAction}
      renderZeroState={() => (
        <Card className={classes.noDocumentsCardContainer}>
          <Body>{text.firstLetterUppercase(i18next.t('noDocumentsUploaded'))}</Body>
        </Card>
      )}
      rowDefinition={rowDefinition}
    />
  );
};
